import React from 'react';
import Icon from '../../../components/AppIcon';

const VendorInfo = ({ vendor }) => {
  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;
    
    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <Icon key={i} name="Star" size={16} color="#DAA520" strokeWidth={0} className="fill-current" />
      );
    }
    
    if (hasHalfStar) {
      stars.push(
        <Icon key="half" name="Star" size={16} color="#DAA520" strokeWidth={1} className="fill-current opacity-50" />
      );
    }
    
    const remainingStars = 5 - Math.ceil(rating);
    for (let i = 0; i < remainingStars; i++) {
      stars.push(
        <Icon key={`empty-${i}`} name="Star" size={16} color="#E8E2DD" strokeWidth={1} />
      );
    }
    
    return stars;
  };

  return (
    <div className="space-y-6">
      {/* Basic Info */}
      <div>
        <div className="flex items-start justify-between mb-2">
          <h1 className="font-heading font-bold text-3xl lg:text-4xl text-text-primary">
            {vendor.name}
          </h1>
          <div className="flex items-center space-x-2">
            {vendor.tags.slice(0, 2).map((tag) => (
              <span
                key={tag}
                className="px-2 py-1 bg-primary-50 text-primary text-xs font-medium rounded-full"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
        
        <div className="flex items-center space-x-4 mb-3">
          <div className="flex items-center space-x-1">
            {renderStars(vendor.rating)}
            <span className="ml-2 font-medium text-text-primary">{vendor.rating}</span>
          </div>
          <span className="text-text-secondary font-caption">
            ({vendor.reviewCount} reviews)
          </span>
          <div className="w-1 h-1 bg-text-muted rounded-full" />
          <span className="text-text-secondary font-caption">
            {vendor.bookingCount} bookings
          </span>
        </div>

        <div className="flex items-center space-x-2 text-text-secondary mb-4">
          <Icon name="MapPin" size={16} color="currentColor" />
          <span className="font-caption">{vendor.location}</span>
          <div className="w-1 h-1 bg-text-muted rounded-full" />
          <span className="font-caption">Serves {vendor.serviceRadius} mile radius</span>
        </div>

        <p className="text-text-secondary font-caption leading-relaxed">
          {vendor.description}
        </p>
      </div>

      {/* Services */}
      <div>
        <h3 className="font-heading font-semibold text-lg text-text-primary mb-3">
          Services Offered
        </h3>
        <div className="flex flex-wrap gap-2">
          {vendor.services.map((service) => (
            <span
              key={service}
              className="px-3 py-1 bg-surface-100 text-text-secondary text-sm rounded-full hover:bg-surface-200 transition-smooth"
            >
              {service}
            </span>
          ))}
        </div>
      </div>

      {/* Cultural Specializations */}
      <div>
        <h3 className="font-heading font-semibold text-lg text-text-primary mb-3">
          Cultural Specializations
        </h3>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
          {vendor.culturalSpecializations.map((culture) => (
            <div
              key={culture.name}
              className="flex items-center space-x-2 p-3 bg-secondary-50 rounded-lg"
            >
              <Icon name="Heart" size={16} color="#DAA520" />
              <div>
                <div className="font-medium text-text-primary text-sm">{culture.name}</div>
                <div className="text-xs text-text-secondary font-caption">{culture.description}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Languages */}
      <div>
        <h3 className="font-heading font-semibold text-lg text-text-primary mb-3">
          Languages Spoken
        </h3>
        <div className="flex flex-wrap gap-2">
          {vendor.languages.map((language) => (
            <div
              key={language}
              className="flex items-center space-x-1 px-3 py-1 bg-primary-50 text-primary text-sm rounded-full"
            >
              <Icon name="MessageCircle" size={14} color="currentColor" />
              <span>{language}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Pricing */}
      <div>
        <h3 className="font-heading font-semibold text-lg text-text-primary mb-3">
          Pricing Range
        </h3>
        <div className="bg-surface-50 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-text-secondary font-caption">Starting from</span>
            <span className="font-heading font-bold text-2xl text-primary">
              ${vendor.pricing.startingPrice.toLocaleString()}
            </span>
          </div>
          <div className="text-sm text-text-secondary font-caption">
            {vendor.pricing.description}
          </div>
          <div className="mt-3 flex items-center space-x-4 text-sm">
            <div className="flex items-center space-x-1">
              <Icon name="Clock" size={14} color="#6B5B4F" />
              <span className="text-text-secondary font-caption">
                {vendor.pricing.duration}
              </span>
            </div>
            <div className="flex items-center space-x-1">
              <Icon name="Users" size={14} color="#6B5B4F" />
              <span className="text-text-secondary font-caption">
                Up to {vendor.pricing.guestCount} guests
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Vendor Tags */}
      <div>
        <h3 className="font-heading font-semibold text-lg text-text-primary mb-3">
          Vendor Highlights
        </h3>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
          {vendor.tags.map((tag) => {
            const tagConfig = {
              'Verified': { icon: 'Shield', color: 'text-success', bg: 'bg-success-50' },
              'Female-Owned': { icon: 'Users', color: 'text-accent', bg: 'bg-accent-50' },
              'Free Consultation': { icon: 'MessageCircle', color: 'text-primary', bg: 'bg-primary-50' },
              'Offers Trials': { icon: 'Eye', color: 'text-secondary', bg: 'bg-secondary-50' },
              'Travel-Ready': { icon: 'MapPin', color: 'text-warning', bg: 'bg-warning-50' },
              'Available This Weekend': { icon: 'Calendar', color: 'text-success', bg: 'bg-success-50' },
              '5-Star Reviews': { icon: 'Star', color: 'text-secondary', bg: 'bg-secondary-50' },
              'Recently Booked': { icon: 'TrendingUp', color: 'text-primary', bg: 'bg-primary-50' }
            };
            
            const config = tagConfig[tag] || { icon: 'Check', color: 'text-text-secondary', bg: 'bg-surface-100' };
            
            return (
              <div
                key={tag}
                className={`flex items-center space-x-2 p-3 ${config.bg} rounded-lg`}
              >
                <Icon name={config.icon} size={16} color="currentColor" className={config.color} />
                <span className="text-sm font-medium text-text-primary">{tag}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default VendorInfo;